import { create } from 'zustand';
import { Invoice, PaymentRecord } from '../types/invoice';
import { ScheduleLine, ScheduleConfig, Asset, UpfrontCharges, NoticeOfAssignment, InvoiceSettings, TDSLiability, TDSRemittance } from '../types/schema';
import type { PostDisbursementCase, EWayBillRecord, TaxInvoiceRecord, AssetInsuranceRecord, ChargeFilingRecord,  AssetSpecRecord, AssetImageRecord, AssetLocationRequest, AssetLocationRecord, Shipment, ShipmentDocFile } from '../types/postDisbursement';
import { recomputeCase, buildInitialCases } from '@/lib/post-disbursement';
import { locationRequestStore } from '@/lib/location-request-store';

export type NachStatus = 'Pending' | 'Active' | 'NotApplicable';

export interface NachRecord {
  status: NachStatus;
  activatedOn?: string;
  startPeriod?: number | string;
  nachType?: "full" | "base_only";
  umrn?: string;
  bankName?: string;
  fileUrl?: string;
  fileName?: string;
  notApplicableReason?: string;
  notApplicableOn?: string;
}

export interface Schedule {
  id: string;
  displayId: string;
  customerID: string;
  status: string;
  lines: ScheduleLine[];
  upfront: UpfrontCharges;
  config: ScheduleConfig;
  assets: Asset[];
  createdAt: string;
  updatedAt: string;
  lenderName?: string;
  createdBy?: string;
  approvedBy?: string;
  invoiceSettings?: InvoiceSettings;
  executedDocuments?: import('../types/schema').ExecutedDocument[];
  ourSignatory?: import('../types/schema').AuthorizedSignatory;
  customerSignatory?: import('../types/schema').AuthorizedSignatory;
  rentalScheduleCode?: string;
  noa?: NoticeOfAssignment;
  buy_back_date?: string;
  noaNachActive?: any;
  avatar?: string;
  individual_asset_id?:string;
  serialNumber?:string;
}

export interface Lender {
  id: string;
  companyName: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  ifscCode: string;
  institution?:string;
}

interface AppState {
  schedules: Schedule[];
  invoices: Invoice[];
  noAs: NoticeOfAssignment[];
  lenders: Lender[];
  tdsLiabilities: TDSLiability[];
  addSchedule: (schedule: Schedule) => void;
  updateSchedule: (scheduleId: string, updates: Partial<Schedule>) => void;
  updateScheduleStatus: (scheduleId: string, status: string) => void;
  updateScheduleSettings: (scheduleId: string, settings: InvoiceSettings) => void;
  addInvoice: (invoice: Invoice) => void;
  addPayment: (invoiceId: string, payment: Omit<PaymentRecord, 'id'>) => void;
  getInvoicesBySchedule: (scheduleId: string) => Invoice[];
  getTotalReceivables: () => number;
  getUpcomingReceivables: (days: number) => number;
  getOverdueInvoices: () => Invoice[];
  addNoA: (noa: NoticeOfAssignment) => void;
  updateNoA: (noaId: string, updates: Partial<NoticeOfAssignment>) => void;
  updateNoAStatus: (noaId: string, status: string) => void;
  getApprovedSchedules: () => Schedule[];
  getAvailableLenders: () => Lender[];
  autoMarkNACHPayments: () => void;
  recordTDSRemittance: (invoiceId: string, remittance: Omit<TDSRemittance, 'id'>) => void;
  getTDSLiability: (invoiceId: string) => TDSLiability | undefined;
  addExecutedDocument: (scheduleId: string, document: import('../types/schema').ExecutedDocument) => void;
  updateExecutedDocument: (scheduleId: string, documentId: string, updates: Partial<import('../types/schema').ExecutedDocument>) => void;
  removeExecutedDocument: (scheduleId: string, documentId: string) => void;

  updateNach: (rsId: string, updates: Partial<NachRecord>) => void;
  upsertAssetSpec: (rsId: string, spec: AssetSpecRecord) => void;
  addAssetImages: (rsId: string, assetId: string, files: File[]) => void;
  removeAssetImage: (rsId: string, imageId: string) => void;
  addAssetInsurance: (rsId: string, ins: Omit<AssetInsuranceRecord, 'id'>) => void;
  removeAssetInsurance: (rsId: string, id: string) => void;
  postDisbursementCases: PostDisbursementCase[];
  updateChargeFiling: (rsId: string, updates: Partial<ChargeFilingRecord>) => void;
  ensurePostDisbCases: () => void;
  
}

export const useAppStore = create<AppState>((set, get) => ({
  schedules: [
    {
      id: "SCH-001",
      displayId: "1",
      customerID: "C1",
      status: "Approved",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-10T00:00:00Z",
    rentalScheduleCode: "RS-001",
      config: {
        customer: {
          customerId: "C1",
          name: "ABC Manufacturing Ltd",
          gstin: "27AABCU9603R1ZX",
          email: "finance@abc.com"
        },
        frequency: "Monthly",
        fundingType: "postGstDiscounting",
        ptpm: 22,
        interestRatePercent: 12,
        tenureMonths: 24,
        commencementDate: "2024-01-01",
        paymentCycleDay: 10,
        securityDepositPercent: 10,
        processingFeePercent: 1
      },
      assets: [
        {
          assetId: "A1",
          description: "Forklift Model X",
          basePrice: 450000,
          hsn: "8427",
          gstRate: 18,
          quantity: 1,
          total: 450000
        }
      ],
      lines: [
        {
          scheduleLineId: "L1",
          periodNo: 'i',
          fromDate: "2024-01-01",
          toDate: "2024-01-31",
          dueDate: "2024-02-10",
          isInterim: false,
          baseRentRaw: 9900,
          baseRentRounded: 9900,
          tdsRaw: 198,
          tdsRounded: 198,
          gstRate: 18,
          gstRaw: 1782,
          gstRounded: 1782,
          totalRaw: 11484,
          totalRounded: 11484,
          totalRoundedAfterTDS: 11286
        },
        {
          scheduleLineId: "L2",
          periodNo: '1',
          fromDate: "2024-01-01",
          toDate: "2024-01-31",
          dueDate: "2024-02-10",
          isInterim: false,
          baseRentRaw: 9900,
          baseRentRounded: 9900,
          tdsRaw: 198,
          tdsRounded: 198,
          gstRate: 18,
          gstRaw: 1782,
          gstRounded: 1782,
          totalRaw: 11484,
          totalRounded: 11484,
          totalRoundedAfterTDS: 11286
        },
      ],
      upfront: {
        securityDepositAmountRaw: 45000,
        securityDepositAmountRounded: 45000,
        processingFeeRaw: 4500,
        processingFeeRounded: 4500,
        feeGSTRaw: 810,
        feeGSTRounded: 810,
        totalRaw: 50310,
        totalRounded: 50310
      }
    }
  ],
  invoices: [],
  noAs: [],
  tdsLiabilities: [],
  lenders: [
    { 
      id: "L1", 
      companyName: "Cholamandalam Investment and Finance Company Limited", 
      bankName: "Cholamandalam Investment and Finance Company Limited",
      accountName: "Cholamandalam Investment and Finance Company Limited",
      accountNumber: "00040310001687",
      ifscCode: "CIFC0000004",
      institution: "Cholamandalam Investment and Finance Company Limited"
    },
    { 
      id: "L2", 
      companyName: "Oxyzo Financial Services Limited", 
      bankName: "IndusInd Bank",
      accountName: "Oxyzo Financial Services Limited",
      accountNumber: "ZOXYZO431E676",
      ifscCode: "INDB0000005",
      institution: "5"
    },
    { 
      id: "L3", 
      companyName: "HDFC Bank", 
      bankName: "HDFC Bank",
      accountName: "HDFC Lending Services",
      accountNumber: "50200012345678",
      ifscCode: "HDFC0001234",
      institution: "3"
    },
    { 
      id: "L4", 
      companyName: "ICICI Bank", 
      bankName: "ICICI Bank",
      accountName: "ICICI Leasing Limited",
      accountNumber: "028505123456",
      ifscCode: "ICIC0000285",
      institution: "4"
    },
    { 
      id: "L5", 
      companyName: "SBI", 
      bankName: "State Bank of India",
      accountName: "SBI Finance Division",
      accountNumber: "30123456789",
      ifscCode: "SBIN0001234",
      institution: "3"
    },
    { 
      id: "L6", 
      companyName: "Axis Bank", 
      bankName: "Axis Bank",
      accountName: "Axis Leasing Solutions",
      accountNumber: "912020012345678",
      ifscCode: "UTIB0001234",
      institution: "1"
    },
    { 
      id: "L7", 
      companyName: "Kotak Mahindra Bank", 
      bankName: "Kotak Mahindra Bank",
      accountName: "Kotak Leasing Services",
      accountNumber: "7311123456",
      ifscCode: "KKBK0001234",
      institution: "2"
    }
  ],
  
  addSchedule: (schedule) => 
    set((state) => {
      // Calculate displayId based on customer's existing schedules
      const customerSchedules = state.schedules.filter(s => s.customerID === schedule.customerID);
      const nextDisplayId = (customerSchedules.length + 1).toString();
      
      return { 
        schedules: [...state.schedules, { ...schedule, displayId: nextDisplayId }] 
      };
    }),
  
  updateSchedule: (scheduleId, updates) =>
    set((state) => {
      const now = new Date();
      return {
        schedules: state.schedules.map((schedule) =>
          schedule.id === scheduleId
            ? { ...schedule, ...updates, updatedAt: now.toISOString() }
            : schedule
        )
      };
    }),
  
  updateScheduleStatus: (scheduleId, status) =>
    set((state) => {
      const now = new Date();
      
      // No invoice creation on schedule approval
      // Invoices will be created only when NoA is generated
      
      return {
        schedules: state.schedules.map((schedule) =>
          schedule.id === scheduleId
            ? { ...schedule, status, updatedAt: now.toISOString() }
            : schedule
        )
      };
    }),

  updateScheduleSettings: (scheduleId, settings) =>
    set((state) => ({
      schedules: state.schedules.map((s) => 
        s.id === scheduleId 
          ? { ...s, invoiceSettings: settings }
          : s
      )
    })),
  
  addInvoice: (invoice) => 
    set((state) => ({ invoices: [...state.invoices, invoice] })),
  
  addPayment: (invoiceId, payment) =>
    set((state) => ({
      invoices: state.invoices.map((invoice) =>
        invoice.invoiceId === invoiceId
          ? {
              ...invoice,
              payments: [
                ...invoice.payments,
                { ...payment, id: `PAY-${Date.now()}` }
              ]
            }
          : invoice
      )
    })),

  getInvoicesBySchedule: (scheduleId) => 
    useAppStore.getState().invoices.filter((inv:Invoice) => inv.scheduleId === scheduleId),
  
  getTotalReceivables: () => 
    useAppStore.getState().invoices.reduce((sum:number, inv:Invoice) => {
      const totalPaid = inv.payments.reduce((s, p) => s + p.amount, 0);
      return sum + (inv.grandTotalRounded - totalPaid);
    }, 0),
  
  getUpcomingReceivables: (days) => 
    useAppStore.getState().invoices
      .filter((inv:Invoice) => {
        const dueDate = new Date(inv.dueDate);
        const today = new Date();
        const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return diffDays <= days && diffDays >= 0;
      })
      .reduce((sum:number, inv:Invoice) => {
        const totalPaid = inv.payments.reduce((s, p) => s + p.amount, 0);
        return sum + (inv.grandTotalRounded - totalPaid);
      }, 0),
  
  getOverdueInvoices: () =>
    useAppStore.getState().invoices.filter((inv:Invoice) => {
      const totalPaid = inv.payments.reduce((s:number, p:PaymentRecord) => s + p.amount, 0);
      const isOverdue = new Date(inv.dueDate) < new Date();
      return isOverdue && totalPaid < inv.grandTotalRounded;
    }),

  addNoA: (noa) => 
    set((state) => {
      const schedule = state.schedules.find(s => s.id === noa.scheduleId);
      
      if (!schedule) {
        return { noAs: [...state.noAs, noa] };
      }

      // Create invoices for ALL rentals when NoA is created
      // Based on funding type: Pre GST, Post GST, Post TDS
      const fundingType = schedule.config.fundingType;
      const newInvoices: Invoice[] = [];
      
      // Create invoices for all schedule lines
      schedule.lines.forEach(line => {
        const isAssignedToLender = noa.selectedRentals.includes(line.scheduleLineId);
        
        // Determine invoice amounts based on funding type and assignment
        let baseAmount = line.baseRentRounded;
        let tdsAmount = line.tdsRounded;
        let grandTotal = line.totalRounded;
        
        // If assigned to lender, follow funding type logic
        if (isAssignedToLender) {
          if (fundingType === 'preGstDiscounting') {
            // Lender gets: Base only
            grandTotal = baseAmount;
            tdsAmount = 0;
          } else if (fundingType === 'postGstDiscounting') {
            // Lender gets: Base + GST
            grandTotal = baseAmount + line.gstRaw;
            tdsAmount = 0;
          } else if (fundingType === 'postTdsDiscounting') {
            // Lender gets: Base + GST - TDS
            grandTotal = line.totalRounded;
          }
        } else {
          // Not assigned: Goes to Astrafin (Base + GST - TDS)
          grandTotal = line.totalRounded;
        }
        
        const invoice: Invoice = {
          invoiceId: `INV-${noa.scheduleId}-R${line.periodNo}`,
          scheduleId: noa.scheduleId,
          customerId: schedule.config.customer.customerId,
          customer: schedule.config.customer,
          type: line.isInterim ? "Advance" : "Rental",
          isManual: false, // Auto-generated from schedule
          invoiceDate: line.fromDate,
          dueDate: line.dueDate,
          baseRounded: baseAmount,
          gstRounded: Math.round(line.gstRaw),
          tdsRounded: tdsAmount,
          grandTotalRounded: grandTotal,
          lineItems: [
            {
              id: `LINE-R${line.periodNo}-${noa.scheduleId}`,
              scheduleID: noa.scheduleId,
              scheduleLineID: line.scheduleLineId,
              description: `${line.isInterim ? 'Interim ' : ''}Rental - Period ${line.periodNo} (${line.fromDate} to ${line.toDate})`,
              baseAmount: baseAmount,
              gstAmount: line.gstRaw,
              tdsAmount: tdsAmount,
              totalAmount: grandTotal,
              fromDate: line.fromDate,
              toDate: line.toDate,
            },
          ],
          payments: [],
          status: "Pending",
          assignedToLender: isAssignedToLender,
          noaId: isAssignedToLender ? noa.noaId : undefined,
          lenderName: isAssignedToLender ? noa.lender : undefined,
          scheduleLineId: line.scheduleLineId,
          fundingMode: fundingType as "A" | "B" | "C",
          periodNo: line.periodNo.toString(),
          rentalScheduleDisplayId: schedule.displayId,
        };
        newInvoices.push(invoice);
      });
      
      return { 
        noAs: [...state.noAs, noa],
        invoices: [...state.invoices, ...newInvoices]
      };
    }),

  updateNoA: (noaId, updates) =>
    set((state) => {
      const now = new Date();
      return {
        noAs: state.noAs.map((noa) =>
          noa.noaId === noaId
            ? { ...noa, ...updates, updatedAt: now.toISOString() }
            : noa
        )
      };
    }),

  updateNoAStatus: (noaId, status) =>
    set((state) => {
      const now = new Date();
      return {
        noAs: state.noAs.map((noa) =>
          noa.noaId === noaId
            ? { ...noa, status: status as any, updatedAt: now.toISOString() }
            : noa
        )
      };
    }),

  getApprovedSchedules: () =>
    useAppStore.getState().schedules.filter(schedule => 
      schedule.status === "Approved" || schedule.status === "Active"
    ),

  getAvailableLenders: () =>
    useAppStore.getState().lenders,

  autoMarkNACHPayments: () => {
    const state = get();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    state.invoices.forEach(invoice => {
      // Only process assigned invoices
      if (!invoice.assignedToLender) return;
      
      const schedule = state.schedules.find(s => s.id === invoice.scheduleId);
      const settings = schedule?.invoiceSettings;
      
      // Check if NACH is setup
      if (!settings?.nachSetup || !settings.nachStartDate) return;
      
      // Check if invoice due date >= NACH start date and <= today
      const nachStartDate = new Date(settings.nachStartDate);
      nachStartDate.setHours(0, 0, 0, 0);
      const dueDate = new Date(invoice.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      
      if (dueDate < nachStartDate) return; // Don't auto-pay invoices before NACH start date
      if (dueDate > today) return; // Don't auto-pay future invoices
      
      // Check if already paid
      const totalPaid = invoice.payments.reduce((s, p) => s + p.amount, 0);
      if (totalPaid >= invoice.grandTotalRounded) return;
      
      // Auto-create payment based on NACH type on the due date
      if (settings.nachType === 'full') {
        // Full amount to lender
        get().addPayment(invoice.id, {
          amount: invoice.grandTotalRounded - totalPaid,
          date: invoice.dueDate,
          payer: 'Lender',
          paymentMode: 'NACH',
          reference: `NACH-AUTO-${invoice.id}`,
          notes: 'Auto-paid via NACH (Full Amount)',
        });
      } else if (settings.nachType === 'base_only') {
        // Base only to lender
        const baseAmount = invoice.baseRounded - invoice.tdsRounded;
        const alreadyPaidBase = invoice.payments
          .filter(p => p.payer === 'Lender')
          .reduce((s, p) => s + p.amount, 0);
        
        if (baseAmount > alreadyPaidBase) {
          get().addPayment(invoice.id, {
            amount: baseAmount - alreadyPaidBase,
            date: invoice.dueDate,
            payer: 'Lender',
            paymentMode: 'NACH',
            reference: `NACH-AUTO-${invoice.id}`,
            notes: 'Auto-paid via NACH (Base Rental Only)',
          });
        }
      }
    });
  },

  recordTDSRemittance: (invoiceId, remittance) =>
    set((state) => {
      const invoice = state.invoices.find(i => i.id === invoiceId);
      if (!invoice) return state;
      
      const existingLiability = state.tdsLiabilities.find(
        l => l.invoiceId === invoiceId
      );
      
      const remittanceWithId = {
        ...remittance,
        id: `TDS-REM-${Date.now()}`
      };
      
      if (existingLiability) {
        return {
          tdsLiabilities: state.tdsLiabilities.map(l =>
            l.invoiceId === invoiceId
              ? {
                  ...l,
                  tdsRemitted: l.tdsRemitted + remittance.amount,
                  tdsPending: l.tdsCollected - (l.tdsRemitted + remittance.amount),
                  remittances: [...l.remittances, remittanceWithId]
                }
              : l
          )
        };
      } else {
        // Create new liability record
        const newLiability: TDSLiability = {
          id: `TDS-${invoiceId}`,
          scheduleId: invoice.scheduleId,
          invoiceId: invoice.id,
          noaId: invoice.noaId!,
          lenderName: invoice.lenderName!,
          tdsCollected: invoice.tdsRounded,
          tdsRemitted: remittance.amount,
          tdsPending: invoice.tdsRounded - remittance.amount,
          remittances: [remittanceWithId]
        };
        
        return {
          tdsLiabilities: [...state.tdsLiabilities, newLiability]
        };
      }
    }),

  getTDSLiability: (invoiceId) =>
    get().tdsLiabilities.find(l => l.invoiceId === invoiceId),

  addExecutedDocument: (scheduleId, document) =>
    set((state) => ({
      schedules: state.schedules.map((schedule) =>
        schedule.id === scheduleId
          ? {
              ...schedule,
              executedDocuments: [
                ...(schedule.executedDocuments || []),
                document
              ],
              updatedAt: new Date().toISOString()
            }
          : schedule
      )
    })),

  updateExecutedDocument: (scheduleId, documentId, updates) =>
    set((state) => ({
      schedules: state.schedules.map((schedule) =>
        schedule.id === scheduleId
          ? {
              ...schedule,
              executedDocuments: (schedule.executedDocuments || []).map((doc) =>
                doc.documentId === documentId
                  ? { ...doc, ...updates }
                  : doc
              ),
              updatedAt: new Date().toISOString()
            }
          : schedule
      )
    })),

    ensurePostDisbCases: () => {
      const state = get();
      if (state.postDisbursementCases.length > 0) return;
      const seeded = buildInitialCases(state.schedules, state.noAs).slice(0, 4);
      if (!seeded.length) return;
  
      // ---- Seed mock Asset Location Verification data for prototype ----
      // Spread sample assets across major Indian cities so the consolidated
      // India map looks populated, plus add one Sent (open) link per demo case
      // so admins can click "Copy URL" and the public verify page resolves.
      const cities: Array<{ name: string; lat: number; lng: number; address: string }> = [
        { name: "Mumbai",    lat: 19.0760, lng: 72.8777, address: "Andheri East, Mumbai, Maharashtra 400069" },
        { name: "Bengaluru", lat: 12.9716, lng: 77.5946, address: "Whitefield, Bengaluru, Karnataka 560066" },
        { name: "Delhi",     lat: 28.6139, lng: 77.2090, address: "Okhla Industrial Area, New Delhi 110020" },
        { name: "Hyderabad", lat: 17.4485, lng: 78.3908, address: "HITEC City, Hyderabad, Telangana 500081" },
        { name: "Chennai",   lat: 13.0827, lng: 80.2707, address: "Guindy Industrial Estate, Chennai 600032" },
        { name: "Pune",      lat: 18.5204, lng: 73.8567, address: "Hinjewadi Phase II, Pune 411057" },
        { name: "Ahmedabad", lat: 23.0225, lng: 72.5714, address: "Sanand GIDC, Ahmedabad 382170" },
        { name: "Kolkata",   lat: 22.5726, lng: 88.3639, address: "Salt Lake Sector V, Kolkata 700091" },
      ];
  
      const now = new Date().toISOString();
      const enriched = seeded.map((c, idx) => {
        if (!c.assetIds.length) return c;
  
        // Verified locations: split assets across 1-2 cities per case
        const locations: AssetLocationRecord[] = [];
        const verifiedRequestId = `LR-DEMO-V-${idx + 1}`;
        const verifiedToken = `demo-verified-${idx + 1}`;
        c.assetIds.forEach((aid, i) => {
          const cityIdx = (idx * 2 + (i % 2)) % cities.length;
          const city = cities[cityIdx];
          // Tiny jitter so pins don't perfectly overlap
          const jLat = city.lat + (Math.random() - 0.5) * 0.04;
          const jLng = city.lng + (Math.random() - 0.5) * 0.04;
          locations.push({
            id: `LOC-DEMO-${idx}-${i}`,
            requestId: verifiedRequestId,
            rsId: c.rsId,
            assetId: aid,
            latitude: Number(jLat.toFixed(6)),
            longitude: Number(jLng.toFixed(6)),
            formattedAddress: city.address,
            accuracyMeters: 20 + Math.floor(Math.random() * 40),
            verifiedAt: now,
            isCurrent: true,
          });
        });
  
        const verifiedReq: AssetLocationRequest = {
          id: verifiedRequestId,
          rsId: c.rsId,
          assetIds: c.assetIds,
          recipientEmail: "ops@customer.example",
          token: verifiedToken,
          createdAt: now,
          expiresAt: new Date(Date.now() + 14 * 86400000).toISOString(),
          sentAt: now,
          status: "Verified",
        };
  
        // Also seed an OPEN (Sent) request for the first 2 cases — so the
        // user can demo the "copy link → open in new tab → confirm" flow.
        const openReq: AssetLocationRequest | null = idx < 2 ? {
          id: `LR-DEMO-O-${idx + 1}`,
          rsId: c.rsId,
          assetIds: c.assetIds.slice(0, Math.min(2, c.assetIds.length)),
          recipientEmail: "site.manager@customer.example",
          customMessage: "Please confirm the current location of the listed assets.",
          token: `demo-open-${idx + 1}`,
          createdAt: now,
          expiresAt: new Date(Date.now() + 14 * 86400000).toISOString(),
          sentAt: now,
          status: "Sent",
        } : null;
  
        const requests = openReq ? [verifiedReq, openReq] : [verifiedReq];
        return { ...c, locations, locationRequests: requests };
      });
  
      set({ postDisbursementCases: enriched });
  
      // Mirror every demo request to the standalone localStorage bucket so
      // the public /verify-asset-location/:token page resolves on any tab.
      enriched.forEach(c => {
        const sched = state.schedules.find(s => s.id === c.rsId);
        (c.locationRequests || []).forEach(req => {
          const assets = req.assetIds.map(aid => {
            const a = sched?.assets.find(x => x.assetId === aid);
            return { assetId: aid, description: a?.description || aid };
          });
          const reqLocs = (c.locations || []).filter(l => l.requestId === req.id);
          locationRequestStore.upsert({
            request: req,
            rsId: c.rsId,
            customerName: c.customerName,
            assets,
            locations: reqLocs,
          });
        });
      });
    },

  removeExecutedDocument: (scheduleId, documentId) =>
    set((state) => ({
      schedules: state.schedules.map((schedule) =>
        schedule.id === scheduleId
          ? {
              ...schedule,
              executedDocuments: (schedule.executedDocuments || []).filter(
                (doc) => doc.documentId !== documentId
              ),
              updatedAt: new Date().toISOString()
            }
          : schedule
      )
    })),

  postDisbursementCases: [],

}));